import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  debounce,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { removeUndefinedAndNull } from 'src/common/removeUndefinedAndNull';
import { useAuthContext } from 'src/components/Context/AuthContext/AuthContext';
import { HttpClient } from 'src/Helpers/http/http';
import { url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { muiStyles } from 'src/Utils/Styles';
import { IAccountType } from '../../Members/MemberAccount/Account/types/AccountTypes';
import DynamicTraineeInvoiceField from './DynamicTraineeInvoiceField';

interface IPropsMember {
  user_training_trainee_id: number;
  user_training_trainee_account_status: string;
  user_training_trainee_email: string;
  user_training_trainee_name: string;
  user_training_trainee_photo: string;
}
const useStyles = muiStyles;
interface ITraineePaymentabeltem {
  training_paymentable_item_id: number;
  training_paymentable_item_name: string;
  training_paymentable_item_bdt_amount: string;
  training_paymentable_item_usd_amount: string;
}
interface IProps {
  handleClose: any;
  setRefresh: (callback: (prev: boolean) => boolean) => void;
}

const CreateTrainingInvoice = ({ handleClose, setRefresh }: IProps) => {
  const { user } = useAuthContext();
  const [allPaymentableItem, setAllPaymentableItem] = useState<
    ITraineePaymentabeltem[]
  >([]);
  const [userMember, setUserMember] = useState<IPropsMember[]>([]);
  const [member, setMember] = useState<IPropsMember | null>(null);
  const [isSearchingTrainee, setIsSearchingTrainee] = useState<boolean>(false);
  const [companyName, seTCompanyName] = useState<string>('');
  const { register, handleSubmit, control, watch, setValue } = useForm();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });
  const withMoneyReceipt = watch('with_mr');
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState<IAccountType[]>([]);
  // console.log({ userMember, allPaymentableItem });
  const [loading, setLoading] = useState(false);
  const paymentMethod = watch('payment_method');
  if (fields.length === 0) {
    append({ id: '', amount: '', discount: '' });
  }

  const items = watch('items');

  const totalItemAmount = items?.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );
  const totalItemDiscount = items?.reduce(
    (sum, item) => sum + Number(item.discount || 0),
    0
  );

  // Fetch paymentable items once when user is logged in
  useEffect(() => {
    if (!user?.user_admin_id) return;
    fetch(`${url}/api/training/payment/get/all/paymentable/item`)
      .then((response) => response.json())
      .then((data) => {
        if (data?.data) {
          setAllPaymentableItem(data.data);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [user?.user_admin_id]);

  // Fetch trainees based on search input with race-condition prevention
  useEffect(() => {
    let active = true;
    setIsSearchingTrainee(true);

    HttpClient.get(
      `/api/training/trainee/get/all/or/status/all?user_training_trainee_name=${companyName}`
    )
      .then((res: any) => {
        if (active) {
          if (res?.success && res?.data) {
            setUserMember(res.data);
          } else if (Array.isArray(res?.data)) {
            setUserMember(res.data);
          } else if (Array.isArray(res)) {
            setUserMember(res);
          } else {
            setUserMember([]);
          }
          setIsSearchingTrainee(false);
        }
      })
      .catch((err) => {
        if (active) {
          console.error(err);
          setUserMember([]);
          setIsSearchingTrainee(false);
        }
      });

    return () => {
      active = false;
    };
  }, [companyName]);

  useEffect(() => {
    (async function () {
      try {
        HttpClient.get(`${url}/api/atab/common-account?CompanyProfileId=2`)
          .then(function (res: any) {
            if (res.success) {
              setAccountData(res.data);
            }
          })
          .catch(function (err) {
            setAccountData([]);
          });
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const onSubmit = async (submitData: any) => {
    setLoading(true);

    try {
      const res: any = await HttpClient.post(
        `${url}/api/admin/training/invoice`,
        removeUndefinedAndNull(submitData)
      );
      if (res.success) {
        Toaster().fire({
          icon: 'success',
          title: res.message,
        });
        setLoading(false);
        handleClose();
        setRefresh((prv) => !prv);
        if (res?.data?.direct_mr) {
          navigate(`/trainee-money-receipt/details/${res.data.id}`);
        } else {
          navigate(`/training-invoice/details/${res.data.id}`);
        }
      } else {
        setLoading(false);
      }
    } catch (err: any) {
      Toaster().fire({
        icon: 'error',
        title: `${err.response?.data?.message}`,
      });
      setLoading(false);
    }
  };
  const handelInputChange = useMemo(
    () =>
      debounce((value: string) => {
        seTCompanyName(value);
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      handelInputChange.clear();
    };
  }, [handelInputChange]);

  return (
    <>
      <Helmet>
        <title>Atab - Create training trainee invoice</title>
      </Helmet>

      <Box
        sx={{
          mx: 4,
          mt: 2,
          maxWidth: '800px',
        }}
        component='form'
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={6}>
            <Controller
              control={control}
              name={`trainee_id`}
              render={({ field }) => (
                <Autocomplete
                  value={member || null}
                  onChange={(_event, newValue) => {
                    field.onChange(
                      newValue ? newValue?.user_training_trainee_id : null
                    );
                    setMember(newValue);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    Number(option?.user_training_trainee_id) ===
                    Number(value?.user_training_trainee_id)
                  }
                  filterOptions={(x) => x}
                  loading={isSearchingTrainee}
                  id='controllable-states-demo1'
                  options={userMember || []}
                  onInputChange={(_event, newInputValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      handelInputChange(newInputValue);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : option?.user_training_trainee_name
                      ? `${option.user_training_trainee_name} (${option.user_training_trainee_id})`
                      : ''
                  }
                  renderOption={(props, option) => (
                    <Box
                      component='li'
                      {...props}
                      key={option.user_training_trainee_id}
                    >
                      {option.user_training_trainee_name} ({option.user_training_trainee_id})
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={field.ref}
                      label='Select trainee'
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSearchingTrainee ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <TextField
              fullWidth
              required
              id='invoice_date'
              label='Invoice Date'
              type='date'
              defaultValue={moment().format('YYYY-MM-DD')}
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              {...register('invoice_date')}
            />
          </Grid>
          <Grid item xs={12}>
            {/* Expense Items */}
            {fields.map((item, index) => {
              return (
                <DynamicTraineeInvoiceField
                  register={register}
                  allPaymentableItem={allPaymentableItem}
                  item={item}
                  index={index}
                  key={index}
                  control={control}
                  remove={remove}
                  fields={fields}
                  setValue={setValue}
                />
              );
            })}
          </Grid>
          <Grid item xs={12}>
            <Button
              type='button'
              variant='contained'
              onClick={() => append({ id: '', amount: '', discount: '' })}
            >
              Add Item
            </Button>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <FormControlLabel
                style={{ marginLeft: '10px' }}
                control={<Checkbox {...register('with_mr')} />}
                label='With Money Receipt'
              />
            </Grid>
          </Grid>

          {withMoneyReceipt && (
            <>
              <Grid
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                item
                xs={12}
                md={12}
                lg={6}
              >
                <TextField
                  fullWidth
                  select
                  required
                  sx={{ mt: 1 }}
                  id='payment_method'
                  label='Payment Method'
                  variant='outlined'
                  {...register('payment_method')}
                >
                  {['Mobile Banking', 'Cash', 'Bank'].map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <FormControl fullWidth required margin='normal'>
                  <InputLabel id='demo-simple-select-label'>Account</InputLabel>
                  <Select
                    labelId='demo-simple-select-label'
                    id='demo-simple-select'
                    {...register('account_id')}
                    label='Account'
                  >
                    {accountData?.map((account) => {
                      return (
                        <MenuItem key={account.id} value={account.id}>
                          {account.account_name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {paymentMethod === 'Bank' && withMoneyReceipt && (
            <>
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  fullWidth
                  required
                  id='BankName'
                  label='Bank Name'
                  type='text'
                  variant='outlined'
                  {...register('BankName')}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  fullWidth
                  id='BranchName'
                  label='Branch Name'
                  type='text'
                  variant='outlined'
                  {...register('BranchName')}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  fullWidth
                  // required
                  id='chequeNo'
                  label='Cheque Number'
                  type='text'
                  variant='outlined'
                  {...register('ChequeNo')}
                />
              </Grid>

              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  fullWidth
                  id='ChequeDate'
                  label='Cheque Date'
                  type='date'
                  // defaultValue={moment().format('YYYY-MM-DD')}
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  {...register('ChequeDate')}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Grid sx={{ mt: 2 }} item xs={12} md={12} lg={12}>
          <TextField
            fullWidth
            required
            rows={3}
            multiline
            id='remark'
            label='Remark'
            type='text'
            variant='outlined'
            {...register('remark')}
          />
        </Grid>
        <Grid
          sx={{ fontSize: '14px', display: 'flex', mt: 1 }}
          item
          xs={12}
          md={12}
          lg={12}
        >
          <p style={{ width: '20%', margin: 0 }}>Invoice Sub Total</p>
          <p style={{ margin: 0 }}>: {totalItemAmount}</p>
        </Grid>
        <Grid
          sx={{ fontSize: '14px', display: 'flex', m: 0 }}
          item
          xs={12}
          md={12}
          lg={12}
        >
          <p style={{ width: '20%', margin: '8px 0' }}>Total Discount (-)</p>
          <p style={{ margin: '8px 0' }}>: {totalItemDiscount}</p>
        </Grid>
        <Grid
          sx={{ fontWeight: 'bold', fontSize: '14px', display: 'flex', m: 0 }}
          item
          xs={12}
          md={12}
          lg={12}
        >
          <p style={{ width: '20%', margin: 0 }}>Invoice Net Total</p>
          <p style={{ margin: 0 }}>: {totalItemAmount - totalItemDiscount}</p>
        </Grid>
        <Box textAlign={'center'} mt={3}>
          {loading ? (
            <LoadingButton
              loading={loading}
              fullWidth
              color='secondary'
              disableElevation
              sx={{
                mt: 3,
                mb: 2,
                py: 1,
              }}
              variant='contained'
            >
              Loading...
            </LoadingButton>
          ) : (
            <Button
              type='submit'
              variant='contained'
              disableElevation
              sx={{
                mt: 3,
                mb: 2,
                py: 1,
              }}
            >
              Create
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CreateTrainingInvoice;
